//	------------------------	------------------------	------------------------
//	Description: Admin/support panel showing scripts from the last few hours
//	------------------------	------------------------	------------------------

import React from 'react'
import moment from 'moment'
import { Button, Loader, Table } from 'semantic-ui-react'

import { useAsync } from 'xAppLib/Hooks/useAsync'
import scripts_list_model_base from 'models/scripts_list_model_base'

const LAST_HOURS = 3

// Time windows to break the activity down by. The 3h block is the whole loaded list (the backend
// already filters to the last 3h); the shorter windows are filtered client-side.
const WINDOWS = [
	{ key: 'h3', title: `Last ${LAST_HOURS} hours`, all: true },
	{ key: 'm60', title: 'Last 60 min', mins: 60 },
	{ key: 'm15', title: 'Last 15 min', mins: 15 },
]

const STAT_COLS = [
	{ key: 'async_appr', label: 'Async' },
	{ key: 'consults', label: 'Consults' },
	{ key: 'cons_scrs', label: 'Cons scrs' },
	{ key: 'subs', label: 'Subs' },
	{ key: 'cosm', label: 'Cosm' },
]

// Bucket a script into one of the fixed stat columns; anything unmatched is aggregated into "other".
function bucket(s) {
	if (s.script_type === 'medcons' || ['premed', 'remed'].includes(s.req_type)) return 'async_appr'
	if (['doccons', 'exprcons'].includes(s.script_type)) return 'consults'
	if (['doccons', 'docconswl'].includes(s.req_type)) return 'cons_scrs'
	if (s.script_type === 'medsub') return 'subs'
	if (s.req_type === 'cosm') return 'cosm'
	return null
}

// Timestamp (ms) representing when the doctor last acted on the record: the last consult "take" if
// available, otherwise the record's creation time (Melbourne-tz "D/M/YY HH:mm:ss").
function activity_ms(s) {
	const ltt = s.ltt
	const m = ltt ? moment(ltt) : moment(s.add_tm, 'D/M/YY HH:mm:ss')
	return m.isValid() ? m.valueOf() : 0
}

function blank_stats() {
	return { async_appr: 0, consults: 0, cons_scrs: 0, subs: 0, cosm: 0 }
}

function group_by_doctor(scripts, now) {
	const groups = {}

	for (const s of scripts) {
		const dr = s.doc_db_data__name || s.doc || 'Unknown'
		const g = groups[dr] || (groups[dr] = {
			doctor: dr,
			windows: { h3: blank_stats(), m60: blank_stats(), m15: blank_stats() },
			other: {},
		})

		const b = bucket(s)
		const age_min = (now - activity_ms(s)) / 60000

		for (const w of WINDOWS) {
			if ((w.all || age_min <= w.mins) && b) g.windows[w.key][b]++
		}

		if (!b) {
			const label = s.script_type + '-' + s.req_type
			g.other[label] = (g.other[label] || 0) + 1
		}
	}

	return Object.values(groups).sort((a, b) => a.doctor.localeCompare(b.doctor))
}

function fmt_other(other) {
	const entries = Object.entries(other)
	if (!entries.length) return ''
	return entries.map(([label, n]) => `${label} (${n})`).join(', ')
}

// Left border to visually separate each window block.
const block_start = { borderLeft: '1px solid rgba(34,36,38,.85)' }
const col_start = { borderLeft: '1px solid rgba(34,36,38,.15)' }

export default function RecentDocsActivity() {

	const { loading, error, data, fn: reload } = useAsync(
		() => scripts_list_model_base.load_scripts_list({ 
																filters: { last_hours: LAST_HOURS, has_doctor: true },
																show_list: 'docs_acts',
														 }),
		[]
	)

	if (loading && !data) return <Loader active inline='centered'>Loading</Loader>

	if (error || data?.res === 'err') return <p className='text-red-600'>Error loading recent scripts</p>

	const scripts = data?.listScripts || []
	const rows = group_by_doctor(scripts, Date.now())

	return (
		<>
			<div className='flex items-center justify-between'>
				<h3>Records in the last {LAST_HOURS} hours ({scripts.length})</h3>
				<Button size='tiny' icon='refresh' content='Reload' loading={loading} disabled={loading} onClick={() => reload()} />
			</div>
			{rows.length === 0
				? <p>No scripts in the last {LAST_HOURS} hours.</p>
				: <Table basic='very' compact celled>
					<Table.Header>
						<Table.Row>
							<Table.HeaderCell rowSpan='2'>Doctor</Table.HeaderCell>
							{WINDOWS.map(w => (
								<Table.HeaderCell key={w.key} colSpan={STAT_COLS.length} textAlign='center' style={block_start}>{w.title}</Table.HeaderCell>
							))}
							<Table.HeaderCell rowSpan='2' style={block_start}>Other</Table.HeaderCell>
						</Table.Row>
						<Table.Row>
							{WINDOWS.map(w => STAT_COLS.map((c, ci) => (
								<Table.HeaderCell key={w.key + '-' + c.key} textAlign='center' style={ci === 0 ? block_start : col_start}>{c.label}</Table.HeaderCell>
							)))}
						</Table.Row>
					</Table.Header>
					<Table.Body>
						{rows.map(r => (
							<Table.Row key={r.doctor}>
								<Table.Cell>{r.doctor}</Table.Cell>
								{WINDOWS.map(w => STAT_COLS.map((c, ci) => (
									<Table.Cell key={w.key + '-' + c.key} textAlign='center' style={ci === 0 ? block_start : col_start}>{r.windows[w.key][c.key] || ''}</Table.Cell>
								)))}
								<Table.Cell style={block_start}>{fmt_other(r.other)}</Table.Cell>
							</Table.Row>
						))}
					</Table.Body>
				</Table>}
		</>
	)
}
